import { useEffect, useRef, useState } from 'react';
import {
    DocumentPlusIcon,
    EllipsisVerticalIcon,
    PencilIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { FloatingMenu } from '../../../FloatingMenu';

interface EntryRowMenuProps {
    onEdit: () => void;
    onAddPatientNote: () => void;
    onDelete: () => void;
    /** True when there's no practice and no linked patient to note against. */
    addPatientNoteDisabled?: boolean;
}

// One quiet "..." trigger per card, portaled via FloatingMenu since these
// cards live in the page's own scroll container.
export function EntryRowMenu({
    onEdit,
    onAddPatientNote,
    onDelete,
    addPatientNoteDisabled = false
}: EntryRowMenuProps) {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open]);

    const handle = (action: () => void) => {
        setOpen(false);
        action();
    };

    return (
        <div className="shrink-0">
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                title="More actions"
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors ${
                    open ? 'bg-gray-100 text-gray-700' : ''
                }`}
            >
                <EllipsisVerticalIcon className="h-5 w-5" />
            </button>
            {open && (
                <FloatingMenu
                    open
                    triggerRef={triggerRef}
                    onClose={() => setOpen(false)}
                    align="right"
                >
                    <div
                        className="bg-white rounded-xl shadow-xl border border-gray-100 p-1.5 min-w-[190px]"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => handle(onEdit)}
                            className="w-full flex items-center gap-2 px-3 h-9 rounded-lg text-[13px] font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            <PencilIcon className="h-4 w-4 text-gray-400" />
                            Edit
                        </button>
                        <button
                            type="button"
                            disabled={addPatientNoteDisabled}
                            title={addPatientNoteDisabled ? 'Link a patient first' : undefined}
                            onClick={() => handle(onAddPatientNote)}
                            className="w-full flex items-center gap-2 px-3 h-9 rounded-lg text-[13px] font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                            <DocumentPlusIcon className="h-4 w-4 text-gray-400" />
                            Add patient note
                        </button>
                        <button
                            type="button"
                            onClick={() => handle(onDelete)}
                            className="w-full flex items-center gap-2 px-3 h-9 rounded-lg text-[13px] font-semibold text-red-600 hover:bg-red-50 transition-colors"
                        >
                            <TrashIcon className="h-4 w-4" />
                            Delete
                        </button>
                    </div>
                </FloatingMenu>
            )}
        </div>
    );
}
